import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import "../styles/admin.css";

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [authorized, setAuthorized] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const SECRET_KEY = "LDC2025ADMIN";
  const [searchTerm, setSearchTerm] = useState("");
  const [filterVehicle, setFilterVehicle] = useState("All");
  const [filterDays, setFilterDays] = useState("all");
  const [newBooking, setNewBooking] = useState(false);

  // ✅ Authorization
  useEffect(() => {
    const key = searchParams.get("key");
    if (key === SECRET_KEY) {
      sessionStorage.setItem("ldc_admin", "true");
      setAuthorized(true);
    } else if (sessionStorage.getItem("ldc_admin") === "true") {
      setAuthorized(true);
    } else {
      navigate("/");
    }
  }, [searchParams, navigate]);

  // ✅ Convert Firestore Timestamp -> Sri Lanka Local Date/Time
  const formatColombo = (ts) => {
    if (!ts?.toDate) return { dateOnly: "", dateTime: "-" };
    const date = ts.toDate();
    const dateTime = new Intl.DateTimeFormat("en-LK", {
      timeZone: "Asia/Colombo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
    const dateOnly = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Colombo",
    }).format(date); // → YYYY-MM-DD
    return { dateOnly, dateTime };
  };

  // ✅ Real-time Firestore listener
  useEffect(() => {
    if (!authorized) return;

    const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => {
        const d = doc.data();
        const fmt = formatColombo(d.createdAt);
        return {
          id: doc.id,
          ...d,
          bookingDateOnly: fmt.dateOnly,
          bookingDateTime: fmt.dateTime,
        };
      });

      // 🔔 Notification for new bookings
      if (bookings.length > 0 && data.length > bookings.length) {
        setNewBooking(true);
        setTimeout(() => setNewBooking(false), 4000);
      }

      setBookings(data);
    });

    return () => unsub();
  }, [authorized]);

  // ✅ Filter Logic
  const filteredBookings = useMemo(() => {
    const now = new Date();
    const today = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Colombo",
    }).format(now);

    const yesterday = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Colombo",
    }).format(new Date(now.getTime() - 24 * 60 * 60 * 1000));

    const sevenDaysAgo = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Colombo",
    }).format(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));

    return bookings.filter((b) => {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        b.ref?.toLowerCase().includes(s) ||
        b.phone?.includes(searchTerm) ||
        b.vehicleType?.toLowerCase().includes(s);
      const matchVehicle =
        filterVehicle === "All" ||
        b.vehicleType?.toLowerCase().includes(filterVehicle.toLowerCase());

      const d = b.bookingDateOnly || "";
      const matchDate =
        filterDays === "today"
          ? d === today
          : filterDays === "yesterday"
          ? d === yesterday
          : filterDays === "7days"
          ? d >= sevenDaysAgo
          : true;

      return matchSearch && matchVehicle && matchDate;
    });
  }, [bookings, searchTerm, filterVehicle, filterDays]);

  if (!authorized) return null;

  // ✅ Render UI
  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>
           Lak Derana Admin Panel{" "}
          {newBooking && <span className="notification-badge">NEW!</span>}
        </h1>
        <button
          className="logout-btn"
          onClick={() => {
            sessionStorage.removeItem("ldc_admin");
            navigate("/");
          }}
        >
          Logout
        </button>
      </div>

      <p className="subtitle">Real-time Booking Overview</p>

      {/* 🔍 Search + Filters */}
      <div className="search-filter">
        <input
          type="text"
          placeholder="Search by Ref #, Phone or Vehicle..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          value={filterVehicle}
          onChange={(e) => setFilterVehicle(e.target.value)}
        >
          <option value="All">All Vehicles</option>
          <option value="Wagon R">Wagon R</option>
          <option value="Toyota Prius">Toyota Prius</option>
          <option value="Honda Vezel">Honda Vezel</option>
          <option value="Passenger Van">Passenger Van</option>
        </select>

        <select
          value={filterDays}
          onChange={(e) => setFilterDays(e.target.value)}
        >
          <option value="all">All Bookings</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7days">Last 7 Days</option>
        </select>
      </div>

      {/* 📋 Booking Table */}
      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Ref #</th>
              <th>Booking Date</th>
              <th>Hire Date</th>
              <th>Time</th>
              <th>Vehicle</th>
              <th>Pickup</th>
              <th>Drop</th>
              <th>Phone</th>
              <th>Distance (km)</th>
              <th>Price (Rs)</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length ? (
              filteredBookings.map((b) => (
                <tr key={b.id}>
                  <td>{b.ref}</td>
                  <td>{b.bookingDateTime}</td>
                  <td>{b.date}</td>
                  <td>{b.time}</td>
                  <td>{b.vehicleType}</td>
                  <td>{b.pickup}</td>
                  <td>{b.drop}</td>
                  <td>{b.phone}</td>
                  <td>{b.distanceKm?.toFixed(1)}</td>
                  <td>{b.price?.toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
